import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Calendar, Pencil, Loader2, Archive } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { getApiBase } from '../../utils/apiBase';
import { toast } from 'sonner';
import { TreeCoverManager } from './TreeCoverManager';

const API = getApiBase();
const MAX_NAME = 120;
const MAX_DESC = 500;

function formatArchiveType(type) {
  if (type === 'memorial') return 'Memorial archive';
  if (type === 'organisation') return 'Organisation archive';
  return 'Family archive';
}

function formatCreatedDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' });
  } catch {
    return iso;
  }
}

/**
 * General archive settings — name (with confirm), description, metadata, cover.
 */
export function ArchiveGeneralSettings({
  tree,
  user,
  canEdit,
  onTreeUpdated,
}) {
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(tree?.name || '');
  const [descriptionDraft, setDescriptionDraft] = useState(tree?.description || '');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [savingDesc, setSavingDesc] = useState(false);

  useEffect(() => {
    setNameDraft(tree?.name || '');
    setDescriptionDraft(tree?.description || '');
    setEditingName(false);
  }, [tree?.id, tree?.name, tree?.description]);

  const trimmedName = nameDraft.trim();
  const nameChanged = trimmedName !== (tree?.name || '').trim();
  const nameInvalid = !trimmedName || trimmedName.length > MAX_NAME;

  const saveName = async () => {
    if (!tree?.id || nameInvalid || !nameChanged) return;
    setSavingName(true);
    try {
      const { data } = await axios.patch(`${API}/trees/${tree.id}`, { name: trimmedName });
      onTreeUpdated?.(data);
      setEditingName(false);
      setConfirmOpen(false);
      toast.success('Archive name updated.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not rename archive.');
    } finally {
      setSavingName(false);
    }
  };

  const saveDescription = async () => {
    if (!tree?.id || !canEdit) return;
    const next = descriptionDraft.trim();
    const prev = (tree?.description || '').trim();
    if (next === prev) return;
    if (next.length > MAX_DESC) {
      toast.error(`Description must be ${MAX_DESC} characters or fewer.`);
      return;
    }
    setSavingDesc(true);
    try {
      const { data } = await axios.patch(`${API}/trees/${tree.id}`, {
        description: next || null,
      });
      onTreeUpdated?.(data);
      toast.success('Archive description saved.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save description.');
    } finally {
      setSavingDesc(false);
    }
  };

  if (!tree) return null;

  return (
    <div className="space-y-6" data-testid="archive-general-settings">
      <div className="rounded-2xl border border-border/50 bg-card/80 p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Archive className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Archive name
            </p>
            {!editingName ? (
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <h3 className="font-serif text-xl text-foreground break-words" data-testid="archive-name-display">
                  {tree.name}
                </h3>
                {canEdit ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 rounded-full px-2 text-muted-foreground"
                    onClick={() => setEditingName(true)}
                    data-testid="archive-name-edit-btn"
                  >
                    <Pencil className="mr-1 h-3.5 w-3.5" />
                    Edit
                  </Button>
                ) : null}
              </div>
            ) : (
              <div className="mt-2 space-y-2">
                <Input
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value.slice(0, MAX_NAME))}
                  maxLength={MAX_NAME}
                  autoFocus
                  data-testid="archive-name-input"
                />
                <p className="text-[11px] text-muted-foreground">
                  {trimmedName.length}/{MAX_NAME} characters
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    className="rounded-full"
                    disabled={nameInvalid || !nameChanged || savingName}
                    onClick={() => setConfirmOpen(true)}
                    data-testid="archive-name-save-btn"
                  >
                    Save name
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-full"
                    onClick={() => {
                      setNameDraft(tree.name || '');
                      setEditingName(false);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Archive type</Label>
            <p className="text-sm text-foreground">{formatArchiveType(tree.archive_type)}</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Owner</Label>
            <p className="text-sm text-foreground break-words">{user?.name || '—'}</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground flex items-center gap-1">
              <Calendar className="h-3 w-3" aria-hidden />
              Created
            </Label>
            <p className="text-sm text-foreground tabular-nums">{formatCreatedDate(tree.created_at)}</p>
          </div>
        </div>

        <div className="mt-6 space-y-2">
          <Label htmlFor="archive-description" className="text-xs text-muted-foreground">
            Archive description <span className="font-normal">(optional subtitle)</span>
          </Label>
          <Textarea
            id="archive-description"
            value={descriptionDraft}
            onChange={(e) => setDescriptionDraft(e.target.value.slice(0, MAX_DESC))}
            disabled={!canEdit}
            rows={3}
            placeholder="A short note about this archive — for your family, your research, or a special collection."
            className="resize-y min-h-[80px]"
            data-testid="archive-description-input"
          />
          {canEdit ? (
            <div className="flex justify-end">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-full"
                disabled={savingDesc}
                onClick={saveDescription}
              >
                {savingDesc ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                Save description
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      {canEdit ? (
        <TreeCoverManager
          treeId={tree.id}
          treeName={tree.name}
          coverImageUrl={tree.cover_image_url}
          coverDisplay={tree.cover_display}
          coverImageWidth={tree.cover_image_width}
          coverImageHeight={tree.cover_image_height}
          canEdit={canEdit}
          onUpdated={(patch) => onTreeUpdated?.(patch)}
        />
      ) : null}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="archive-rename-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif font-normal">Rename this archive?</AlertDialogTitle>
            <AlertDialogDescription className="leading-relaxed">
              This changes the archive name across your dashboard, exports and shared views. It does not change any
              people, stories or documents.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={savingName}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={savingName}
              onClick={(e) => {
                e.preventDefault();
                saveName();
              }}
            >
              {savingName ? 'Saving…' : 'Confirm rename'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default ArchiveGeneralSettings;
